/**
 * Client half of the extractor proxy: seven callables, plus the descriptor cache.
 *
 * Nothing here talks to the extractor directly. The shared secret lives only in
 * Firestore, read only by the callables, so the browser never holds it — which
 * is the whole reason the proxy exists (memo §3: `VITE_CF_PROXY_SECRET` is
 * already readable in DevTools today, and this key grants "start arbitrary
 * brand crawls from James's home IP").
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit as limitTo,
  onSnapshot,
  orderBy,
  query,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';
import { normalizeBrandOverrides, type ScraperDescriptor } from './extractorMatching';
import { EXTRACTION_JOBS_COLLECTION, type ExtractorJobDoc } from './extractorJobs';

export type { ScraperDescriptor } from './extractorMatching';

export interface ExtractorConfigStatus {
  /** Both halves present — the Run button can actually do something. */
  configured: boolean;
  baseUrl: string | null;
  secretConfigured: boolean;
  /** The secret came from the function's environment, not Firestore. */
  secretFromEnvironment: boolean;
  /** `descriptorBrandId → watchBrands doc id`; `{}` when none are set. */
  brandOverrides: Record<string, string>;
  updatedAt: number | null;
  updatedBy: string | null;
}

export interface ExtractorHealth {
  reachable: boolean;
  authOk: boolean;
  scraperCount: number;
  baseUrl: string;
  detail?: string;
}

export interface RunJobOptions {
  mode?: string;
  limit?: number;
  offset?: number;
  concurrency?: number;
  politenessDelayMs?: number;
  familySlugs?: string[];
}

export async function getExtractorConfigStatus(): Promise<ExtractorConfigStatus> {
  const fn = httpsCallable<unknown, ExtractorConfigStatus>(
    functions,
    'getExtractorConfig_status',
  );
  const result = await fn({});
  return {
    ...result.data,
    /* Normalized on the way out too — the callable and this screen must agree. */
    brandOverrides: normalizeBrandOverrides(result.data.brandOverrides),
  };
}

/**
 * Save any part. The secret is omitted when unchanged — the field is write-only,
 * so there is no stored value for the form to send back.
 *
 * `brandOverrides`, by contrast, is sent WHOLE: it is a readable map the screen
 * holds in full, and a partial save could not express a deletion. Sending `{}`
 * therefore clears every override, which is what an operator emptying the last
 * box means.
 */
export async function saveExtractorConfig(config: {
  baseUrl?: string;
  secret?: string;
  brandOverrides?: Record<string, string>;
}): Promise<void> {
  const fn = httpsCallable<typeof config, { success: boolean }>(
    functions,
    'saveExtractorConfig',
  );
  await fn(config);
  if (config.brandOverrides) clearBrandOverridesCache();
}

export async function testExtractorConnection(): Promise<ExtractorHealth> {
  const fn = httpsCallable<unknown, ExtractorHealth>(functions, 'testExtractorConnection');
  const result = await fn({});
  return result.data;
}

// ---------------------------------------------------------------------------
// Brand overrides
// ---------------------------------------------------------------------------

const EXTRACTOR_CONFIG_PATH = ['admin_config', 'extractor_config'] as const;

/**
 * In-flight and settled reads of the override map, shared across components.
 *
 * The brand editor asks for this on every open and the Run modal asks again a
 * click later; both want the same one-document answer. Memory rather than
 * `sessionStorage` (which the descriptor catalogue uses) because the cost being
 * avoided is a Firestore read on a hot path, not a network round trip on reload
 * — and a stale override surviving a refresh would be a confusing thing to debug.
 */
let overridesCache: Promise<Record<string, string>> | null = null;

/** Drop the cached map — after a save, or when a caller wants a fresh read. */
export function clearBrandOverridesCache(): void {
  overridesCache = null;
}

/**
 * The `descriptorBrandId → brandId` map, read straight from Firestore.
 *
 * Deliberately NOT through `getExtractorConfig_status`: the non-secret half of
 * the config lives on a document every authenticated client may read (the secret
 * is a subcollection below it precisely so this one can be), and the surfaces
 * that need the map — the brand editor's Run button gate, the Run modal — are
 * asking a question about which UI to render, not performing an admin action.
 *
 * Fails soft to `{}`. An unreadable config means the matcher behaves exactly as
 * it did before overrides existed, which is a worse answer than the real map but
 * a much better one than no Run button at all.
 */
export async function fetchBrandOverrides(force = false): Promise<Record<string, string>> {
  if (force) clearBrandOverridesCache();
  if (!overridesCache) {
    overridesCache = getDoc(doc(db, ...EXTRACTOR_CONFIG_PATH))
      .then((snap) => normalizeBrandOverrides(snap.data()?.['brandOverrides']))
      .catch((err) => {
        console.warn('Could not read extractor brand overrides', err);
        overridesCache = null;
        return {};
      });
  }
  return overridesCache;
}

interface GetScrapersResponse {
  scrapers: ScraperDescriptor[];
  etag: string | null;
  notModified: boolean;
}

export async function runExtractorJob(input: {
  extractorId: string;
  brandId: string;
  brandSlug: string;
  options?: RunJobOptions;
}): Promise<{ jobId: string }> {
  const fn = httpsCallable<typeof input, { jobId: string }>(functions, 'runExtractorJob');
  const result = await fn(input);
  return result.data;
}

/**
 * Start the generic image downloader over pasted bulk-import JSON. The text is
 * sent verbatim; the callable stores it under the job's Storage folder and
 * starts the job on the extractor. Progress is the usual job doc —
 * `subscribeToExtractorJob` — and the rewritten JSON is the usual
 * `getExtractorJobResult`.
 */
export async function startImageDownloadJob(input: {
  json: string;
  brandId: string;
  brandSlug: string;
}): Promise<{ jobId: string }> {
  const fn = httpsCallable<typeof input, { jobId: string }>(functions, 'startImageDownloadJob');
  const result = await fn(input);
  return result.data;
}

export async function cancelExtractorJob(jobId: string): Promise<void> {
  const fn = httpsCallable<{ jobId: string }, { jobId: string }>(
    functions,
    'cancelExtractorJob',
  );
  await fn({ jobId });
}

/**
 * Close a job the extractor is never going to close itself.
 *
 * The escape hatch for a runner that died mid-flight. `cancelExtractorJob` is a
 * proxy — it asks the extractor to stop, and the extractor is what writes the
 * doc — so it can do nothing for a job whose extractor is gone, which is
 * exactly the job that needs closing. This writes the doc directly instead,
 * server-side with the Admin SDK, because `extraction_jobs` is closed to client
 * writes (`allow write: if false`) and should stay that way.
 *
 * The server decides whether the job really is stuck, on the same thresholds
 * the button uses. A row that came back to life between the render and the
 * click is refused rather than closed out from under a live crawl.
 */
export async function forceCloseExtractorJob(jobId: string): Promise<void> {
  const fn = httpsCallable<{ jobId: string }, { jobId: string; status: string }>(
    functions,
    'forceCloseExtractorJob',
  );
  await fn({ jobId });
}

export interface DeleteJobsResult {
  /** Ids no longer in the collection — including ones already gone. */
  deleted: string[];
  /** Live jobs the server refused, with the reason to show the operator. */
  skipped: Array<{ jobId: string; reason: string }>;
  /**
   * Deleted jobs whose `extractor-jobs/{jobId}/` Storage folder could not be
   * wiped. The row is gone regardless. Absent from servers deployed before the
   * wipe existed.
   */
  storageErrors?: Array<{ jobId: string; error: string }>;
}

/**
 * Remove job rows from the history, and wipe each deleted job's image folder
 * (`extractor-jobs/{jobId}/`) in Storage. Nothing is sent to the extractor, and
 * the result file on its disk is untouched. Images a bulk import committed are
 * safe — the import copies them to the reference's own path.
 *
 * Batched into ONE call because old jobs arrive in batches: a month of failed
 * runs is tens of rows, and thirty sequential callables to tidy them is thirty
 * cold starts. A queued or running job comes back in `skipped` rather than
 * failing the whole request.
 */
export async function deleteExtractorJobs(jobIds: string[]): Promise<DeleteJobsResult> {
  const fn = httpsCallable<{ jobIds: string[] }, DeleteJobsResult>(
    functions,
    'deleteExtractorJob',
  );
  const result = await fn({ jobIds });
  return result.data;
}

export async function getExtractorJobResult(
  jobId: string,
): Promise<{ result: unknown; bytes: number }> {
  const fn = httpsCallable<{ jobId: string }, { result: unknown; bytes: number }>(
    functions,
    'getExtractorJobResult',
  );
  const result = await fn({ jobId });
  return result.data;
}

// ---------------------------------------------------------------------------
// Job history
// ---------------------------------------------------------------------------

/**
 * Live subscription to the newest jobs across every brand.
 *
 * Ordered and capped server-side: the collection grows without bound and the
 * screen shows a page of it. Brand and status narrowing happen client-side
 * (`filterJobs` in `extractorJobs.ts`) rather than as extra `where` clauses,
 * which keeps this to one composite-index-free query — switching a status
 * filter should not be able to produce a "this query requires an index" error
 * against a collection that has no index deployed for it.
 */
export function subscribeToExtractorJobs(
  onJobs: (jobs: ExtractorJobDoc[]) => void,
  onError: (error: Error) => void,
  max = 100,
): () => void {
  const q = query(
    collection(db, EXTRACTION_JOBS_COLLECTION),
    orderBy('createdAt', 'desc'),
    limitTo(max),
  );

  return onSnapshot(
    q,
    (snapshot) => {
      onJobs(
        snapshot.docs.map((d) => ({
          ...(d.data() as Omit<ExtractorJobDoc, 'jobId'>),
          jobId: d.id,
        })),
      );
    },
    (err) => onError(err instanceof Error ? err : new Error(String(err))),
  );
}

/**
 * Live subscription to ONE job, for the card the Run modal turns into.
 *
 * The same source the jobs screen reads, at the granularity a single run needs.
 * Not a poll: the runner updates `extraction_jobs/{jobId}` as it goes, so a
 * snapshot listener moves the status pill queued → running → completed with no
 * timer on this side, and there is no callable that answers "how is job X
 * doing" to poll anyway.
 *
 * `onError` fires rather than throwing, and the caller is expected to degrade to
 * a link rather than to break: this collection's read rule lives in watchlock,
 * so a missing deployment there is a permission error here.
 */
export function subscribeToExtractorJob(
  jobId: string,
  onJob: (job: ExtractorJobDoc | null) => void,
  onError: (error: Error) => void,
): () => void {
  return onSnapshot(
    doc(db, EXTRACTION_JOBS_COLLECTION, jobId),
    (snapshot) => {
      if (!snapshot.exists()) {
        onJob(null);
        return;
      }
      onJob({ ...(snapshot.data() as Omit<ExtractorJobDoc, 'jobId'>), jobId: snapshot.id });
    },
    (err) => onError(err instanceof Error ? err : new Error(String(err))),
  );
}

// ---------------------------------------------------------------------------
// Descriptor cache
// ---------------------------------------------------------------------------

const CACHE_KEY = 'wk.extractorScrapers.v1';

interface CachedCatalogue {
  scrapers: ScraperDescriptor[];
  etag: string | null;
}

/**
 * Session-scoped, ETag-validated.
 *
 * The catalogue is fixed for the lifetime of the extractor process, so the
 * extractor computes one ETag at startup and answers a matching
 * `If-None-Match` with a 304. Caching in `sessionStorage` rather than memory
 * means a page reload does not re-download it, and validating rather than
 * trusting means restarting the extractor with a new module still shows up:
 * the ETag is content-addressed, so a restart that changed nothing is still a
 * 304 and a restart that added a module is not.
 *
 * A failed read of the cache is not a failure of the fetch — a corrupted or
 * quota-blocked `sessionStorage` degrades to fetching every time.
 */
function readCache(): CachedCatalogue | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedCatalogue;
    return Array.isArray(parsed?.scrapers) ? parsed : null;
  } catch {
    return null;
  }
}

function writeCache(value: CachedCatalogue): void {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(value));
  } catch {
    /* Cache is an optimisation; a full or disabled sessionStorage is fine. */
  }
}

/** Drop the cached catalogue — used after the base URL changes. */
export function clearScrapersCache(): void {
  try {
    sessionStorage.removeItem(CACHE_KEY);
  } catch {
    /* See writeCache. */
  }
}

/**
 * The scraper catalogue, from cache when the extractor says it is unchanged.
 *
 * `force` bypasses the cached copy but still sends the ETag, so a manual
 * refresh is a 304 and no body when nothing has changed.
 */
export async function fetchScrapers(force = false): Promise<ScraperDescriptor[]> {
  const cached = readCache();
  if (cached && !force && cached.scrapers.length > 0 && cached.etag === null) {
    /* No ETag to validate against — trust the session copy rather than refetch. */
    return cached.scrapers;
  }

  const fn = httpsCallable<{ etag?: string }, GetScrapersResponse>(
    functions,
    'getExtractorScrapers',
  );
  const response = await fn(cached?.etag ? { etag: cached.etag } : {});
  const data = response.data;

  if (data.notModified && cached) return cached.scrapers;

  writeCache({ scrapers: data.scrapers, etag: data.etag });
  return data.scrapers;
}

/**
 * The message an operator can act on, out of a callable rejection.
 *
 * The callable already distinguishes "not configured" from "asleep" from
 * "wrong key" (`extractorHttp.ts`), and that distinction is the useful part —
 * so it is relayed rather than replaced with a generic failure string.
 */
export function extractorErrorMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err) {
    const message = String((err as { message: unknown }).message);
    return message.replace(/^FirebaseError:\s*/, '');
  }
  return String(err);
}

/**
 * One newest-first page of jobs, read once.
 *
 * The same query `subscribeToExtractorJobs` uses — same ordering, same cap, so
 * no additional index — but fetched rather than subscribed. The config screen
 * derives each module's last run from this (`lastRunByExtractorId`) and has no
 * use for live updates: it is a column on a settings table, not a job monitor.
 *
 * Resolves to `[]` rather than throwing when the read is denied. Reads of
 * `extraction_jobs` are rules-gated, and a config screen that fails to load
 * because one derived column could not be computed would be a far worse
 * outcome than the column reading "—".
 */
export async function fetchRecentExtractorJobs(max = 200): Promise<ExtractorJobDoc[]> {
  try {
    const snapshot = await getDocs(
      query(
        collection(db, EXTRACTION_JOBS_COLLECTION),
        orderBy('createdAt', 'desc'),
        limitTo(max),
      ),
    );
    return snapshot.docs.map((d) => ({
      ...(d.data() as Omit<ExtractorJobDoc, 'jobId'>),
      jobId: d.id,
    }));
  } catch (err) {
    console.warn('[extractorService] could not read extraction jobs for last-run', err);
    return [];
  }
}
